import { useEffect, useMemo } from 'react';

import { PreviewHeader } from '@/features/workflows/components/simple/preview-screens';
import { cn } from '@/lib/utils';

import type { SessionDetailDto, SessionStepDto } from '../../schemas/sessionSchemas';
import { formatResultDateTime } from '../../sessionDateTime';
import { extractError } from '../pan/pan.utils';

const STATUS_STYLE: Record<string, string> = {
  Passed: 'bg-[#DCFCE7] text-[#166534]',
  Review: 'bg-[#FEF9C3] text-[#854D0E]',
  Failed: 'bg-[#FEE2E2] text-[#991B1B]',
  Pending: 'bg-[#F3F4F6] text-[#374151]',
};

// Read-only Utility Bill detail. The stored result carries ONLY the connection type
// that ran (normalizer emits clean keys), so we render whichever of the known labels
// are present — one label map covers all three connection types.
const UTILITY_FIELD_LABELS: Array<{ key: string; label: string }> = [
  // Electricity
  { key: 'bill_no', label: 'Bill Number' },
  { key: 'consumer_number', label: 'Consumer Number' },
  { key: 'bill_issue_date', label: 'Bill Issue Date' },
  { key: 'bill_due_date', label: 'Bill Due Date' },
  { key: 'bill_date', label: 'Bill Date' },
  { key: 'consumer_name', label: 'Consumer Name' },
  { key: 'address', label: 'Address' },
  { key: 'mobile_number', label: 'Mobile Number' },
  { key: 'email_address', label: 'Email Address' },
  { key: 'bill_amount', label: 'Bill Amount' },
  { key: 'amount_payable', label: 'Amount Payable' },
  { key: 'total_amount', label: 'Total Amount' },
  { key: 'bill_overdue_flag', label: 'Bill Overdue Flag' },
  // PNG (keys not already listed)
  { key: 'due_date', label: 'Due Date' },
  { key: 'customer_name', label: 'Customer Name' },
  { key: 'customer_address', label: 'Customer Address' },
  { key: 'mobile', label: 'Mobile Number' },
  { key: 'email', label: 'Email Address' },
  { key: 'duplicate_bill_flag', label: 'Duplicate Bill Flag' },
  { key: 'due_date_elapsed_flag', label: 'Due Date Elapsed Flag' },
  // LPG (keys not already listed)
  { key: 'status', label: 'Status' },
  { key: 'consumer_no', label: 'Consumer Number' },
  { key: 'consumer_address', label: 'Consumer Address' },
  { key: 'pin', label: 'PIN Code' },
  { key: 'city_town', label: 'City / Town' },
  { key: 'aadhaar_no', label: 'Aadhaar Number' },
  { key: 'distributor_name', label: 'Distributor Name' },
  { key: 'bank_name', label: 'Bank Name' },
  { key: 'ifsc_code', label: 'IFSC Code' },
  { key: 'bank_account_no', label: 'Bank Account Number' },
  { key: 'consumer_email', label: 'Email Address' },
  { key: 'consumer_contact', label: 'Contact Number' },
  { key: 'last_booking_date', label: 'Last Booking Date' },
  { key: 'total_refill_consumed', label: 'Total Refills Consumed' },
  { key: 'subsidized_refill_consumed', label: 'Subsidised Refills Consumed' },
  { key: 'approximate_subsidy_availed', label: 'Approx. Subsidy Availed' },
  { key: 'given_up_subsidy', label: 'Given Up Subsidy' },
  // Shared risk / metadata
  { key: 'name_match_score', label: 'Name Match Score' },
  { key: 'duplicate_consumer_flag', label: 'Duplicate Consumer Flag' },
  { key: 'dormant_connection_flag', label: 'Dormant Connection Flag' },
  { key: 'aadhaar_not_linked_flag', label: 'Aadhaar Not Linked Flag' },
  { key: 'verification_status', label: 'Status' },
  { key: 'verification_attempts', label: 'Verification Attempts' },
  { key: 'verified_at', label: 'Verified At' },
  { key: 'method_used', label: 'Method Used' },
];

export function UtilityBillDetail({
  step,
  formId,
  onPendingChange,
  onReadyChange,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
  formId: string;
  onPendingChange: (pending: boolean) => void;
  onReadyChange: (ready: boolean) => void;
}) {
  useEffect(() => {
    onPendingChange(false);
    onReadyChange(false);
  }, [onPendingChange, onReadyChange]);

  const error = extractError(step.resultJson);
  const claims = useMemo(() => extractClaims(step.resultJson), [step.resultJson]);

  return (
    <form id={formId} onSubmit={(e) => e.preventDefault()} className="flex flex-col gap-4">
      <PreviewHeader title="Utility Bill Verification" subtitle="Connection verification result" />

      <div className="flex items-center justify-between rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5">
        <span className="text-sm text-[#6B7280]">Status</span>
        <span
          className={cn(
            'rounded-full px-2.5 py-0.5 text-xs font-semibold',
            STATUS_STYLE[step.status] ?? STATUS_STYLE.Pending,
          )}
        >
          {step.status}
        </span>
      </div>

      {claims.length > 0 && (
        <div className="flex flex-col divide-y divide-[#F3F4F6] rounded-lg border border-[#E5E7EB]">
          {claims.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between px-3 py-2.5">
              <span className="text-sm text-[#6B7280]">{label}</span>
              <span className="text-sm font-medium text-[#111827]">{value}</span>
            </div>
          ))}
        </div>
      )}

      {error && step.status === 'Failed' && <p className="text-xs text-[#BF3636]">{error}</p>}
    </form>
  );
}

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (key === 'name_match_score') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : `${n.toFixed(0)}%`;
  }
  if (key === 'verified_at') {
    return formatResultDateTime(value);
  }
  return String(value);
}

function extractClaims(resultJson: string | null): [string, string][] {
  if (!resultJson) return [];
  let parsed: Record<string, unknown> | null = null;
  try {
    const p = JSON.parse(resultJson);
    parsed = p && typeof p === 'object' ? (p as Record<string, unknown>) : null;
  } catch {
    return [];
  }
  if (!parsed) return [];
  const out: [string, string][] = [];
  const seen = new Set<string>();
  for (const { key, label } of UTILITY_FIELD_LABELS) {
    if (key in parsed && parsed[key] !== null && parsed[key] !== '' && !seen.has(label)) {
      out.push([label, formatValue(key, parsed[key])]);
      seen.add(label);
    }
  }
  return out;
}
